import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import moment from 'utils/moment';
import DateCalendar from 'ScheduleTimeOffBatch/components/DateCalendar';
import { DateRange, applyRangePick, setRangeEdge, singleDayRange } from 'ScheduleTimeOffBatch/services/date-range';
import { daysBetween } from 'TimeOffList/services/format';
import { validateClosure } from '../services/closure-rules';

export interface ClosureFormValue {
  startDate: string;
  endDate: string;
  reason: string;
}

interface Props {
  initial?: ClosureFormValue;
  saving: boolean;
  onCancel: () => void;
  onSave: (value: ClosureFormValue) => void;
}

const FMT = 'YYYY-MM-DD';

const ClosureForm: React.FC<Props> = ({ initial, saving, onCancel, onSave }) => {
  const today = moment().format(FMT);
  const [range, setRange] = useState<DateRange>(() =>
    initial ? { start: initial.startDate, end: initial.endDate, phase: 'start' } : singleDayRange(today));
  const [view, setView] = useState(() => {
    const m = moment(initial?.startDate || today, FMT);
    return { year: m.year(), month: m.month() };
  });
  const [reason, setReason] = useState(initial?.reason || '');

  const jumpTo = useCallback((k: string) => {
    if (!k) return;
    setView({ year: Number(k.slice(0, 4)), month: Number(k.slice(5, 7)) - 1 });
  }, []);

  const shiftMonth = (delta: number) => setView(v => {
    const m = moment({ year: v.year, month: v.month, day: 1 }).add(delta, 'month');
    return { year: m.year(), month: m.month() };
  });

  const pickSingle = (k: string) => { setRange(singleDayRange(k)); jumpTo(k); };

  const error = validateClosure(range.start, range.end, reason);
  // The reason prompt is not an error to shout about while the field is untouched.
  const showError = error && reason.trim() ? error : null;
  const days = daysBetween(range.start, range.end);

  return (
    <Wrap>
      <Content>
        <DateCalendar
          viewYear={view.year}
          viewMonth={view.month}
          rangeStart={range.start}
          rangeEnd={range.end}
          pickPhase={range.phase}
          onPickDay={k => { setRange(r => applyRangePick(r, k)); jumpTo(k); }}
          onChangeFrom={k => { setRange(r => setRangeEdge(r, 'start', k)); jumpTo(k); }}
          onChangeTo={k => { setRange(r => setRangeEdge(r, 'end', k)); jumpTo(k); }}
          onPrevMonth={() => shiftMonth(-1)}
          onNextMonth={() => shiftMonth(1)}
          onToday={() => pickSingle(today)}
          onTomorrow={() => pickSingle(moment().add(1, 'day').format(FMT))}
        />
        <Column>
          <Label>Reason <span className="req">*</span></Label>
          <ReasonInput
            value={reason}
            maxLength={255}
            rows={4}
            placeholder="e.g. Holiday, renovation (required)"
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReason(e.target.value)}
          />
          <Note>
            The shop will be closed for {days} {days > 1 ? 'days' : 'day'}. Maximum 13 days. Existing appointments
            are kept and listed after saving.
          </Note>
          {showError && <ErrorText>{showError}</ErrorText>}
        </Column>
      </Content>
      <Footer>
        <Ghost onClick={onCancel} disabled={saving}>Cancel</Ghost>
        <Primary disabled={!!error || saving} onClick={() => onSave({ startDate: range.start, endDate: range.end, reason: reason.trim() })}>
          {saving ? 'Saving...' : 'Save'}
        </Primary>
      </Footer>
    </Wrap>
  );
};

export default ClosureForm;

const Wrap = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
`;

const Column = styled.div`
  flex: 1;
  min-width: 0;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Label = styled.span`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-1);
  .req { color: #F05326; }
`;

const ReasonInput = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  padding: 11px 14px;
  border: 1px solid var(--border-1);
  border-radius: var(--r-md);
  font-size: 14px;
  font-family: inherit;
  color: var(--fg-1);
  resize: none;
  outline: none;
  &:focus { border-color: var(--border-focus); box-shadow: var(--ring-focus); }
`;

const Note = styled.p`
  margin: 0;
  font-size: 12px;
  color: var(--fg-3);
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #F05326;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 24px;
  border-top: 1px solid var(--border-1);
`;

const Ghost = styled.button`
  padding: 11px 22px;
  border-radius: var(--r-md);
  border: 1px solid var(--border-1);
  background: #fff;
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Primary = styled.button`
  padding: 11px 28px;
  border-radius: var(--r-md);
  border: none;
  background: var(--brand-navy);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;
